import { capitalize } from "lodash";
import PATHS from "routes/paths";
import { ANIMATION_STAGES, STAGE_ORDER } from "./animationConstants";

export const ANIMATION_DURATION = 1000;
export const CHARACTER_DELAY = 40; // 40ms between characters

/**
 * Calculates slide distance based on element and canvas bounds
 * @param {Object} elementBounds - Element bounding rectangle
 * @param {Object} canvasBounds - Canvas bounding rectangle
 * @param {string} direction - Slide direction
 * @param {number} zoom - Current zoom level
 * @returns {number} Slide distance in pixels
 */
const calculateSlideDistance = (
  elementBounds,
  canvasBounds,
  direction,
  zoom
) => {
  let distance = 0;

  if (direction === "left") {
    distance =
      (elementBounds.left - canvasBounds.left + elementBounds.width) / zoom;
  } else if (direction === "right") {
    distance =
      (canvasBounds.right - elementBounds.right + elementBounds.width) / zoom;
  } else if (direction === "up") {
    distance =
      (elementBounds.top - canvasBounds.top + elementBounds.height) / zoom;
  } else if (direction === "down") {
    distance =
      (canvasBounds.bottom - elementBounds.bottom + elementBounds.height) /
      zoom;
  }

  // Ensure we have enough distance to slide completely off screen
  return Math.max(distance, elementBounds.height / zoom);
};

/**
 * Gets CSS transform string for slide animation
 * @param {number} distance - Distance to slide
 * @param {string} direction - Slide direction (the direction the element will move)
 * @param {boolean} isSlideOut - Whether this is a slide-out animation
 * @param {number} scaleX - Horizontal flip state (1 = normal, -1 = flipped)
 * @param {number} scaleY - Vertical flip state (1 = normal, -1 = flipped)
 * @returns {string} CSS transform value
 */
const getSlideTransform = (
  distance,
  direction,
  isSlideOut = false,
  scaleX = 1,
  scaleY = 1
) => {
  // For normal position (no transform)
  const noTransform = "translateX(0) translateY(0)";

  // When an element is flipped (scaleX: -1 or scaleY: -1), the coordinate system is inverted
  // We need to compensate by inverting the translate direction to maintain visual consistency
  const flipCompensationX = scaleX === -1 ? -1 : 1;
  const flipCompensationY = scaleY === -1 ? -1 : 1;

  // Direction indicates where the element will move
  // For slide-in: Start from opposite side of target direction
  // For slide-out: Move in the target direction
  if (direction === "left") {
    const baseTransform = isSlideOut ? -distance : distance;
    return `translateX(${baseTransform * flipCompensationX}px)`; // Compensate for horizontal flip
  } else if (direction === "right") {
    const baseTransform = isSlideOut ? distance : -distance;
    return `translateX(${baseTransform * flipCompensationX}px)`; // Compensate for horizontal flip
  } else if (direction === "up") {
    const baseTransform = isSlideOut ? -distance : distance;
    return `translateY(${baseTransform * flipCompensationY}px)`; // Compensate for vertical flip
  } else if (direction === "down") {
    const baseTransform = isSlideOut ? distance : -distance;
    return `translateY(${baseTransform * flipCompensationY}px)`; // Compensate for vertical flip
  }
  return noTransform;
};

/**
 * Helper function to sort animations by stage order
 * @param {Object} a - First animation
 * @param {Object} b - Second animation
 * @returns {number} Sort order
 */
const sortByStageOrder = (a, b) => {
  const aIndex = STAGE_ORDER.indexOf(a.stage);
  const bIndex = STAGE_ORDER.indexOf(b.stage);
  return aIndex - bIndex;
};

/**
 * Gets the status of an "animate out" (end stage) animation
 * @param {Array} cssAnimations - Array of stage animations
 * @param {number} currentPlayingTime - Current time in milliseconds
 * @param {Object} elementData - Element data for duration calculations
 * @returns {string} 'waiting', 'active', 'completed', or 'none'
 */
const getAnimateOutStatus = (
  cssAnimations,
  currentPlayingTime,
  elementData
) => {
  if (!cssAnimations || cssAnimations.length === 0) {
    return "none";
  }

  // Find the "end" stage animation with transition "out"
  const endAnimation = cssAnimations.find(
    anim => anim.stage === ANIMATION_STAGES.END && anim.transition === "out"
  );

  if (!endAnimation) {
    return "none"; // No animate out animation
  }

  // Calculate animation timing
  const animationStartTime = endAnimation.delay || 0;
  const animationDuration = calculateSingleAnimationDuration(
    endAnimation,
    elementData
  );
  const animationEndTime = animationStartTime + animationDuration;

  if (currentPlayingTime < animationStartTime) {
    return "waiting"; // Animation hasn't started yet
  } else if (
    currentPlayingTime >= animationStartTime &&
    currentPlayingTime <= animationEndTime
  ) {
    return "active"; // Animation is currently playing
  } else {
    return "completed"; // Animation has finished
  }
};

/**
 * Determines which animation should be active at a given time
 * @param {Array} cssAnimations - Array of stage animations
 * @param {number} currentPlayingTime - Current time in milliseconds
 * @param {Object} elementData - Element data for duration calculations
 * @returns {Object|null} Active animation or null
 */
export const getActiveAnimationAtTime = (
  cssAnimations,
  currentPlayingTime,
  elementData
) => {
  if (!cssAnimations || cssAnimations.length === 0) {
    return null;
  }

  // First, check for any preview animations - they take priority over time-based animations
  const previewAnimation = cssAnimations.find(anim => anim.isPreview);
  if (previewAnimation) {
    return {
      ...previewAnimation,
      relativeTime: 0, // Preview animations start from the beginning
      isActive: true
    };
  }

  // Sort animations by stage order
  const sortedAnimations = [...cssAnimations].sort(sortByStageOrder);

  for (const animation of sortedAnimations) {
    // Use absolute delay from timeline start, not cumulative
    const animationStartTime = animation.delay || 0;
    const animationDuration = calculateSingleAnimationDuration(
      animation,
      elementData
    );
    const animationEndTime = animationStartTime + animationDuration;

    if (
      currentPlayingTime >= animationStartTime &&
      currentPlayingTime <= animationEndTime
    ) {
      const activeAnimation = {
        ...animation,
        relativeTime: currentPlayingTime - animationStartTime,
        isActive: true
      };

      return activeAnimation;
    }
  }

  return null;
};

/**
 * Processes a single animation (either from array or legacy object)
 * @param {Object} animation - Animation configuration
 * @param {Object} elementData - Element data
 * @param {number} pageId - Page ID
 * @param {number} zoom - Zoom level
 * @param {number} relativeTime - Time relative to animation start (for timing adjustments)
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {Object} Animation styles
 */
const processSingleAnimation = (
  animation,
  elementData,
  pageId,
  zoom,
  relativeTime = 0,
  isPaused = false,
  currentPlayingTime = 0
) => {
  // Create temporary elementData with the single animation for existing functions
  const tempElementData = {
    ...elementData,
    cssAnimation: animation
  };

  // Check for text-level animations (both character and word)
  if (animation.textType === "letter" || animation.textType === "word") {
    // For multiple animations, we need to override the delay to 0 since timing is handled by getActiveAnimationAtTime
    const adjustedElementData = {
      ...tempElementData,
      cssAnimation: {
        ...animation,
        delay: 0,
        originalDelay: animation.delay
      }
    };
    return createCharacterAnimationStyles(
      adjustedElementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const animationName = getAnimateCssClass(
    animation.name,
    animation.direction,
    animation.transition,
    false,
    scaleX,
    scaleY
  );

  const animationStyles = {
    // When paused: use negative delay to "seek" to exact frame position
    // When playing: use 0ms delay to let CSS animation run naturally from start
    // The animation stage system (getActiveAnimationAtTime) handles which animation plays when
    animationDelay: isPaused ? `-${relativeTime}ms` : "0ms",
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: animationName,
    animationIterationCount: 1,
    animationFillMode: "both",
    // Pause the CSS animation when timeline is paused
    animationPlayState: isPaused ? "paused" : "running"
  };

  // For slide animations, we need to modify how they're applied
  if (animation.name === "slide") {
    return createSlideAnimation(
      tempElementData,
      pageId,
      zoom,
      isPaused,
      relativeTime
    );
  }

  if (PATHS.isRenderer(window.location.pathname)) {
    animationStyles.animationPlayState = "paused";
  }

  return animationStyles;
};

/**
 * Processes character-level slide animations with contextual distances
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused (for Phase 4.3)
 * @param {number} currentPlayingTime - Current timeline time in milliseconds (for Phase 4.3)
 * @returns {string} Processed HTML with character spans
 */
const processCharacterSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // Calculate slide distance using same logic as whole element slides
  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        // Create a word wrapper to maintain word boundaries
        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "pre"; // Preserve whitespace within words

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          // Create unique class for this character
          const charClass = `char-slide-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Set initial styles for slide animation
          const existingStyle = span.getAttribute("style") || "";
          const initialStyles = [
            "display: inline-block",
            "position: relative", // Enable z-index stacking
            "vertical-align: baseline", // Maintain text baseline
            "white-space: pre" // Preserve whitespace
          ];

          // Set initial transform based on transition type
          if (transition === "in") {
            const initialTransform = getSlideTransform(
              slideDistance,
              direction,
              false,
              scaleX,
              scaleY
            );
            initialStyles.push(`transform: ${initialTransform}`);
            initialStyles.push("opacity: 0");
          } else {
            initialStyles.push("transform: translateX(0) translateY(0)");
            initialStyles.push("opacity: 1");
          }

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Preserve original element's layout properties
      const computedStyle = window.getComputedStyle(node);
      const layoutProperties = [
        "display",
        "position",
        "float",
        "clear",
        "margin",
        "padding",
        "width",
        "height",
        "text-align",
        "vertical-align",
        "line-height",
        "white-space",
        "word-spacing",
        "letter-spacing",
        "text-indent"
      ];

      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      // Add computed layout properties that aren't already in inline styles
      layoutProperties.forEach(prop => {
        if (!elementStyle.includes(prop) && computedStyle[prop] !== "") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${
            computedStyle[prop]
          }`;
        }
      });

      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
        node.setAttribute("style", elementStyle);
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  // Generate CSS animations for character slides
  const styleId = `char-slide-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing) or first creation
  if (existingStyle && !isPaused) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  // Create keyframes and rules for each character
  for (let i = 0; i < characterIndex; i++) {
    const charClass = `.char-slide-${animationId}-${i}`;
    const charDelay = isPreview
      ? i * CHARACTER_DELAY
      : (delay || 0) + i * CHARACTER_DELAY;
    const keyframeName = `charSlide${direction}${transition}-${animationId}-${i}`;

    // Create character-specific keyframe
    if (transition === "in") {
      const fromTransform = getSlideTransform(
        slideDistance,
        direction,
        false,
        scaleX,
        scaleY
      );
      const toTransform = "translateX(0) translateY(0)";

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 0;
          }
          to {
            transform: ${toTransform};
            opacity: 1;
          }
        }
      `;
    } else {
      const fromTransform = "translateX(0) translateY(0)";
      const toTransform = getSlideTransform(
        slideDistance,
        direction,
        true,
        scaleX,
        scaleY
      );

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 1;
          }
          to {
            transform: ${toTransform};
            opacity: 0;
          }
        }
      `;
    }

    // Calculate animation delay and play-state based on pause/scrub state
    // Preview mode should always play, regardless of timeline pause state
    let animationDelay;
    let animationPlayState = "";
    if (isPaused && !isPreview) {
      // When paused, calculate how far into this character's animation we are
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - show initial state
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Character animation has finished - show final state with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - scrub to exact position with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      // When playing or previewing
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - use original delay
        animationDelay = `${charDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Character animation has finished - show final state
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - position it correctly and keep playing
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    // Apply animation to character with proper timing function
    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        will-change: transform, opacity;
      }
    `;
  }

  styleTag.textContent = cssRules;
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes HTML string to wrap each character in animated spans
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data (for slide animations)
 * @param {number} pageId - Page ID (for slide animations)
 * @param {number} zoom - Zoom level (for slide animations)
 * @param {boolean} isPaused - Whether timeline is paused (for Phase 4.3)
 * @param {number} currentPlayingTime - Current timeline time in milliseconds (for Phase 4.3)
 * @returns {string} Processed HTML with character spans
 */
const processCharactersForAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const { name } = animationConfig;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  if (name === "slide") {
    return processCharacterSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        // Create a word wrapper that allows proper text wrapping
        const wordWrapper = document.createElement("span");
        wordWrapper.style.cssText = `
          display: inline-block;
          white-space: nowrap;
          vertical-align: baseline;
          word-break: break-word;
        `
          .replace(/\s+/g, " ")
          .trim();

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          // For multiple animations, delay is 0 (timeline timing handled elsewhere)
          // For single animations or preview, use the actual delay
          const charDelay = isPreview
            ? characterIndex * CHARACTER_DELAY
            : (delay || 0) + characterIndex * CHARACTER_DELAY;

          // Create unique class for this character
          const charClass = `char-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Add animate.css classes
          const animateClass = getAnimateCssClass(
            name,
            direction,
            transition,
            true,
            scaleX,
            scaleY
          );
          span.classList.add("animate__animated", animateClass);

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Set animation properties via inline style (append to existing styles)
          const existingStyle = span.getAttribute("style") || "";

          // Calculate animation styles based on pause/scrub state
          // Preview mode should always play, regardless of timeline pause state
          let animationStyles;
          if (isPaused && !isPreview) {
            // When paused, calculate how far into this character's animation we are
            const charStartTime = charDelay;
            const relativeTime = relativeTimeToAnimation - charStartTime;

            if (relativeTime < 0) {
              // Character hasn't started yet - show initial state
              animationStyles = [
                `animation-delay: 0ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Character animation has finished - show final state with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedDelay = -(
                ANIMATION_DURATION - RENDER_COMPENSATION_MS
              );
              animationStyles = [
                `animation-delay: ${compensatedDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            } else {
              // Character is mid-animation - scrub to exact position with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
              animationStyles = [
                `animation-delay: -${compensatedTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            }
          } else {
            // When playing or previewing
            const charStartTime = charDelay;
            const relativeTime = relativeTimeToAnimation - charStartTime;

            if (relativeTime < 0) {
              // Character hasn't started yet - use original delay
              animationStyles = [
                `animation-delay: ${charDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Character animation has finished - show final state
              animationStyles = [
                `animation-delay: -${ANIMATION_DURATION}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            } else {
              // Character is mid-animation - position it correctly and keep playing
              animationStyles = [
                `animation-delay: -${relativeTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `display: inline-block`,
                `vertical-align: baseline`
              ];
            }
          }

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            animationStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Collect styles from this element to pass to children
      const currentStyles = { ...inheritedStyles };

      // Get inline styles from this element
      let elementStyle = node.getAttribute("style") || "";
      if (elementStyle) {
        // Combine inherited styles with current element styles
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level animations with style preservation
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with word spans
 */
const processWordAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    name,
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // For slide animations, use custom slide processing
  if (name === "slide") {
    return processWordSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";

        // Add animate.css classes
        const animateClass = getAnimateCssClass(
          name,
          direction,
          transition,
          true,
          scaleX,
          scaleY
        );
        wordWrapper.classList.add("animate__animated", animateClass);

        // Set animation properties via inline style
        const wordDelay = isPreview
          ? wordIndex * CHARACTER_DELAY
          : delay + wordIndex * CHARACTER_DELAY;

        // Calculate animation styles based on pause/scrub state
        // Preview mode should always play, regardless of timeline pause state
        let animationStyles;
        if (isPaused && !isPreview) {
          // When paused, calculate how far into this word's animation we are
          const wordStartTime = wordDelay;
          const relativeTime = relativeTimeToAnimation - wordStartTime;

          if (relativeTime < 0) {
            // Word hasn't started yet - show initial state
            animationStyles = [
              `animation-delay: 0ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else if (relativeTime >= ANIMATION_DURATION) {
            // Word animation has finished - show final state with compensation
            const RENDER_COMPENSATION_MS = 30;
            const compensatedDelay = -(
              ANIMATION_DURATION - RENDER_COMPENSATION_MS
            );
            animationStyles = [
              `animation-delay: ${compensatedDelay}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else {
            // Word is mid-animation - scrub to exact position with compensation
            const RENDER_COMPENSATION_MS = 30;
            const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
            animationStyles = [
              `animation-delay: -${compensatedTime}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          }
        } else {
          // When playing or previewing
          const wordStartTime = wordDelay;
          const relativeTime = relativeTimeToAnimation - wordStartTime;

          if (relativeTime < 0) {
            // Word hasn't started yet - use original delay
            animationStyles = [
              `animation-delay: ${wordDelay}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`
            ];
          } else if (relativeTime >= ANIMATION_DURATION) {
            // Word animation has finished - show final state
            animationStyles = [
              `animation-delay: -${ANIMATION_DURATION}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else {
            // Word is mid-animation - position it correctly and keep playing
            animationStyles = [
              `animation-delay: -${relativeTime}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`
            ];
          }
        }

        wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false); // Clone without children
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex} animate__animated ${getAnimateCssClass(
            name,
            direction,
            transition,
            true,
            scaleX,
            scaleY
          )}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";

          const wordDelay = isPreview
            ? wordIndex * CHARACTER_DELAY
            : delay + wordIndex * CHARACTER_DELAY;

          // Calculate animation styles based on pause/scrub state
          // Preview mode should always play, regardless of timeline pause state
          let animationStyles;
          if (isPaused && !isPreview) {
            // When paused, calculate how far into this word's animation we are
            const wordStartTime = wordDelay;
            const relativeTime = relativeTimeToAnimation - wordStartTime;

            if (relativeTime < 0) {
              // Word hasn't started yet - show initial state
              animationStyles = [
                `animation-delay: 0ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Word animation has finished - show final state with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedDelay = -(
                ANIMATION_DURATION - RENDER_COMPENSATION_MS
              );
              animationStyles = [
                `animation-delay: ${compensatedDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else {
              // Word is mid-animation - scrub to exact position with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
              animationStyles = [
                `animation-delay: -${compensatedTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            }
          } else {
            // When playing or previewing
            const wordStartTime = wordDelay;
            const relativeTime = relativeTimeToAnimation - wordStartTime;

            if (relativeTime < 0) {
              // Word hasn't started yet - use original delay
              animationStyles = [
                `animation-delay: ${wordDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Word animation has finished - show final state
              animationStyles = [
                `animation-delay: -${ANIMATION_DURATION}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else {
              // Word is mid-animation - position it correctly and keep playing
              animationStyles = [
                `animation-delay: -${relativeTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`
              ];
            }
          }

          wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level slide animations with custom keyframes
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with word spans
 */
const processWordSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";
        wordWrapper.style.position = "relative";
        wordWrapper.style.verticalAlign = "baseline";

        if (transition === "in") {
          const initialTransform = getSlideTransform(
            slideDistance,
            direction,
            false,
            scaleX,
            scaleY
          );
          wordWrapper.style.transform = initialTransform;
          wordWrapper.style.opacity = "0";
        } else if (transition === "out") {
          wordWrapper.style.transform = "translateX(0) translateY(0)";
          wordWrapper.style.opacity = "1";
        }

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false);
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";
          wordWrapper.style.position = "relative";
          wordWrapper.style.verticalAlign = "baseline";

          if (transition === "in") {
            const initialTransform = getSlideTransform(
              slideDistance,
              direction,
              false,
              scaleX,
              scaleY
            );
            wordWrapper.style.transform = initialTransform;
            wordWrapper.style.opacity = "0";
          } else if (transition === "out") {
            wordWrapper.style.transform = "translateX(0) translateY(0)";
            wordWrapper.style.opacity = "1";
          }

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);

  const styleId = `word-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing) or first creation
  if (existingStyle && !isPaused) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  wordNodes.forEach((data, wordWrapper) => {
    const wordClass = `.word-${animationId}-${data.index}`;
    const wordDelay = isPreview
      ? data.index * CHARACTER_DELAY
      : delay + data.index * CHARACTER_DELAY;

    const keyframeName = `wordSlide${direction}${transition}-${animationId}-${data.index}`;

    if (transition === "in") {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${getSlideTransform(
              slideDistance,
              direction,
              false,
              scaleX,
              scaleY
            )};
            opacity: 0;
          }
          to {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
        }
      `;
    } else {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
          to {
            transform: ${getSlideTransform(
              slideDistance,
              direction,
              true,
              scaleX,
              scaleY
            )};
            opacity: 0;
          }
        }
      `;
    }

    // Calculate animation delay and play-state based on pause/scrub state
    // Preview mode should always play, regardless of timeline pause state
    let animationDelay;
    let animationPlayState = "";
    if (isPaused && !isPreview) {
      // When paused, calculate how far into this word's animation we are
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        // Word hasn't started yet - show initial state
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Word animation has finished - show final state with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Word is mid-animation - scrub to exact position with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      // When playing or previewing
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        // Word hasn't started yet - use original delay
        animationDelay = `${wordDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Word animation has finished - show final state
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        // Word is mid-animation - position it correctly and keep playing
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    cssRules += `
      ${wordClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        display: inline-block;
        white-space: inherit;
        position: relative;
        z-index: ${data.index + 1};
        will-change: transform, opacity;
      }
      ${wordClass} * {
        display: inline-block;
        white-space: inherit;
      }
    `;
  });

  styleTag.textContent = cssRules;
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Creates character-level animation styles and processes displayValue
 * @param {Object} elementData - Element data containing animation config
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {Object} Animation configuration with processed HTML
 */
const createCharacterAnimationStyles = (
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    name,
    direction,
    transition,
    delay,
    originalDelay,
    isPreview,
    textType
  } = elementData.cssAnimation;
  const animationId = `text-anim-${elementData.uniqueId}`;

  if (!elementData.displayValue) {
    return null;
  }

  // Process the displayValue based on animation type
  const processedHTML =
    textType === "word"
      ? processWordAnimation(
          elementData.displayValue,
          animationId,
          { name, direction, transition, delay, originalDelay, isPreview },
          elementData,
          pageId,
          zoom,
          isPaused,
          currentPlayingTime
        )
      : processCharactersForAnimation(
          elementData.displayValue,
          animationId,
          { name, direction, transition, delay, originalDelay, isPreview },
          elementData,
          pageId,
          zoom,
          isPaused,
          currentPlayingTime
        );

  return {
    processedDisplayValue: processedHTML,
    animationName: "none",
    animationDuration: "0ms"
  };
};

/**
 * Creates a custom slide animation for an element
 * @param {Object} elementData - Element data containing animation config
 * @param {string} animationName - Base animation name
 * @param {string} customAnimName - Custom animation name
 * @param {string} direction - Animation direction
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} relativeTime - Time relative to animation start
 * @returns {Object} Animation styles object
 */
const createSlideAnimation = (
  elementData,
  pageId,
  zoom,
  isPaused = false,
  relativeTime = 0
) => {
  const { name, direction, transition } = elementData.cssAnimation;
  const customAnimName = `custom${name}-${direction}-${transition}-${elementData.uniqueId}`;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // When paused, calculate the exact position and apply transform directly
  // CSS animations don't reliably update when changing animation-delay mid-animation
  if (isPaused) {
    // Add a small compensation for render latency (typically 1-2 frames at 60fps)
    // This accounts for the delay between CSS animation frame and pause calculation
    const RENDER_COMPENSATION_MS = 30; // ~2 frames at 60fps
    const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;

    // Calculate progress through animation (0 to 1)
    const progress = Math.min(
      1,
      Math.max(0, compensatedTime / ANIMATION_DURATION)
    );

    // Get the slide distance
    const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
    const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: 0,
      height: 0
    };
    const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
    const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
      width: elementData.width || 0,
      height: elementData.height || 0
    };

    const slideDistance = calculateSlideDistance(
      ELEMENT_BOUNDS,
      CANVAS_PAGE_BOUNDS,
      direction,
      zoom
    );

    // Flip compensation for paused animations
    const flipCompensationX = scaleX === -1 ? -1 : 1;
    const flipCompensationY = scaleY === -1 ? -1 : 1;

    // Calculate current position based on progress
    let currentTransform;
    let currentOpacity;

    if (transition === "in") {
      // Slide in: from offset to 0
      // Interpolate between start and end based on progress
      if (direction === "down") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateY(${-currentOffset *
          flipCompensationY}px)`;
      } else if (direction === "up") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateY(${currentOffset * flipCompensationY}px)`;
      } else if (direction === "left") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateX(${currentOffset * flipCompensationX}px)`;
      } else if (direction === "right") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateX(${-currentOffset *
          flipCompensationX}px)`;
      }

      currentOpacity = 1; // Slide-in animations are always visible
    } else {
      // Slide out: from 0 to offset
      if (direction === "down") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateY(${currentOffset * flipCompensationY}px)`;
      } else if (direction === "up") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateY(${-currentOffset *
          flipCompensationY}px)`;
      } else if (direction === "left") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateX(${-currentOffset *
          flipCompensationX}px)`;
      } else if (direction === "right") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateX(${currentOffset * flipCompensationX}px)`;
      }

      currentOpacity = 1 - progress;
    }

    return {
      position: "relative",
      transform: currentTransform,
      opacity: currentOpacity,
      visibility: "visible"
    };
  }

  const animationStyles = {
    // Use 0ms delay to let CSS animation run naturally from start
    // Never change this during playback to avoid restarts
    animationDelay: "0ms",
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: customAnimName,
    animationIterationCount: 1,
    animationFillMode: "both",
    animationPlayState: "running"
  };

  if (transition === "in") {
    // For slide-in animations, element should be visible initially
    animationStyles.opacity = "1";
    animationStyles.visibility = "visible";
    // Use !important to ensure visibility overrides any other styles
    animationStyles.opacity = "1 !important";
  }

  // Before creating a new style tag, check if one with this ID already exists
  const customAnimationId = `custom-animation-${customAnimName}`;
  const existingStyleTag = document.getElementById(customAnimationId);

  // Don't recreate styles if they already exist (for both preview and playing modes)
  if (existingStyleTag) {
    return animationStyles;
  }

  // Clean up any old slide animations with different directions/transitions for this element
  const allDirections = ["up", "down", "left", "right"];
  const allTransitions = ["in", "out"];

  allDirections.forEach(dir => {
    allTransitions.forEach(trans => {
      if (dir !== direction || trans !== transition) {
        const oldAnimName = `custom${name}-${dir}-${trans}-${elementData.uniqueId}`;
        const oldStyleId = `custom-animation-${oldAnimName}`;
        const oldStyleTag = document.getElementById(oldStyleId);
        if (oldStyleTag) {
          oldStyleTag.remove();
        }
      }
    });
  });

  // Create new style tag since none exists
  const styleTag = document.createElement("style");
  styleTag.id = customAnimationId;

  let keyframeDefinition = "";

  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };
  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  // Use shared utility to calculate slide distance
  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  // Define animations based on transition type (in or out)
  if (transition === "in") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        0% {
          transform: ${getSlideTransform(
            slideDistance,
            direction,
            false,
            scaleX,
            scaleY
          )};
          opacity: 1;
        }
        100% {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
      }
    `;
  } else if (transition === "out") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        from {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
        to {
          transform: ${getSlideTransform(
            slideDistance,
            direction,
            true,
            scaleX,
            scaleY
          )};
          opacity: 0;
        }
      }
    `;

    // For slide-out, ensure the element stays hidden after animation
    animationStyles.opacity = "0";
  }

  styleTag.textContent = keyframeDefinition;
  document.head.appendChild(styleTag);

  // Add an animation end listener to remove the style tag when animation completes
  const element = document.getElementById(elementData.uniqueId);
  if (element) {
    const onAnimationEnd = () => {
      const styleElement = document.getElementById(customAnimationId);
      if (styleElement) {
        styleElement.remove();
      }
      element.removeEventListener("animationend", onAnimationEnd);
    };

    element.addEventListener("animationend", onAnimationEnd);
  }

  return animationStyles;
};

/**
 * Maps animation names to animate.css classes
 * @param {string} animationName - Animation name
 * @param {string} animationDirection - Animation direction
 * @param {string} animationTransition - Animation transition (in/out)
 * @param {boolean} isTextboxAnimation - Whether this is a textbox animation
 * @param {number} scaleX - Horizontal flip state (1 = normal, -1 = flipped)
 * @param {number} scaleY - Vertical flip state (1 = normal, -1 = flipped)
 * @returns {string} Animate.css class name
 */
const getAnimateCssClass = (
  animationName,
  animationDirection,
  animationTransition,
  isTextboxAnimation = false,
  scaleX = 1,
  scaleY = 1
) => {
  // Map our animation names to animate.css equivalents
  let name = animationName;
  if (animationName === "boing") {
    name = "bounce";
  }

  let direction = animationDirection;
  if (animationDirection === "horizontal") {
    direction = "x";
  } else if (animationDirection === "vertical") {
    direction = "y";
  } else if (animationDirection === "center") {
    direction = "";
    // library directions should be flipped for slide-in animations
  } else if (animationDirection === "left" && animationTransition === "in") {
    direction = "right";
  } else if (animationDirection === "right" && animationTransition === "in") {
    direction = "left";
  }

  // Compensate for element flipping:
  // When an element is flipped, animate.css animations are also affected by the flip transform
  // We need to swap the direction to maintain the intended visual animation
  if (
    name === "bounce" ||
    name === "back" ||
    name === "zoom" ||
    name === "fade"
  ) {
    // Handle vertical flip - swap up/down directions
    if (scaleY === -1) {
      if (direction === "up") {
        direction = "down";
      } else if (direction === "down") {
        direction = "up";
      }
    }

    // Handle horizontal flip - swap left/right directions
    if (scaleX === -1) {
      if (direction === "left") {
        direction = "right";
      } else if (direction === "right") {
        direction = "left";
      }
    }
  }

  return `${isTextboxAnimation ? "animate__" : ""}${name}${capitalize(
    animationTransition
  )}${capitalize(direction)}`;
};

/**
 * Converts cssAnimation object to inline style properties
 * @param {Object} cssAnimation - Animation configuration object
 * @param {number} cssAnimation.delay - Animation delay in milliseconds
 * @param {number} cssAnimation.duration - Animation duration in milliseconds
 * @param {string} cssAnimation.direction - Animation name/direction
 * @param {string} cssAnimation.transition - Animation transition
 * @param {boolean} isPlaying - Whether the animation is playing
 * @returns {Object|null} Animation style object or null if no animation
 */
export const getAnimationStyles = (
  elementData,
  isPlaying,
  pageId,
  zoom,
  currentPlayingTime = 0,
  isPaused = false
) => {
  if (!elementData?.cssAnimation) {
    return null;
  }

  // Handle multiple animations (new format)
  if (Array.isArray(elementData.cssAnimation)) {
    // When paused, treat it like playing so we can apply animation styles
    if (
      !isPlaying &&
      !isPaused &&
      !elementData.cssAnimation.some(anim => anim.isPreview)
    ) {
      // Even when not playing, check if element should be hidden due to start stage animation
      const startAnimation = elementData.cssAnimation.find(
        anim => anim.stage === ANIMATION_STAGES.START
      );

      if (startAnimation) {
        // Element has a start animation - should be hidden initially
        return {
          opacity: 0,
          visibility: "hidden"
        };
      }

      return null;
    }

    const activeAnimation = getActiveAnimationAtTime(
      elementData.cssAnimation,
      currentPlayingTime,
      elementData
    );

    if (!activeAnimation) {
      const startAnimation = elementData.cssAnimation.find(
        anim => anim.stage === ANIMATION_STAGES.START
      );

      if (startAnimation) {
        const startAnimationTime = startAnimation.delay || 0;

        if (currentPlayingTime < startAnimationTime) {
          // Element has a start animation that hasn't started yet - should be hidden
          return {
            opacity: 0,
            visibility: "hidden"
          };
        }
      }

      // SECOND: Check animate-out status
      const animateOutStatus = getAnimateOutStatus(
        elementData.cssAnimation,
        currentPlayingTime,
        elementData
      );

      if (animateOutStatus === "completed") {
        // Element has completed an animate out animation and should remain hidden
        return {
          opacity: 0,
          visibility: "hidden"
        };
      } else if (animateOutStatus === "waiting") {
        // Element is waiting for animate out to start, should be visible
        return {
          opacity: 1,
          visibility: "visible"
        };
      } else if (animateOutStatus === "active") {
        // Animation should be active but getActiveAnimationAtTime returned null
        // This is a fallback - find and process the active animate-out animation manually
        const endAnimation = elementData.cssAnimation.find(
          anim =>
            anim.stage === ANIMATION_STAGES.END && anim.transition === "out"
        );

        if (endAnimation) {
          const animationStartTime = endAnimation.delay || 0;
          const relativeTime = currentPlayingTime - animationStartTime;

          return processSingleAnimation(
            endAnimation,
            elementData,
            pageId,
            zoom,
            relativeTime,
            isPaused,
            currentPlayingTime
          );
        }
      }
      return {
        opacity: 1,
        visibility: "visible"
      };
    }

    // Clean up existing animation styles for multiple animations
    const animationId = `text-anim-${elementData.uniqueId}`;
    const charStyleId = `char-slide-animation-${animationId}`;
    const wordStyleId = `word-animation-${animationId}`;
    const customAnimName = `custom${activeAnimation.name}-${activeAnimation.direction}-${activeAnimation.transition}-${elementData.uniqueId}`;
    const customAnimationId = `custom-animation-${customAnimName}`;

    const isInPreviewMode = activeAnimation.isPreview;
    const hasExistingStyles =
      document.getElementById(charStyleId) ||
      document.getElementById(wordStyleId) ||
      document.getElementById(customAnimationId);

    // Don't clean up styles during active character/word animations
    // Character and word animations manage their own lifecycle
    const isCharacterOrWordAnimation =
      activeAnimation.textType === "letter" ||
      activeAnimation.textType === "word";

    // Only clean up styles if we're not in preview mode AND no existing styles are found AND not a character/word animation
    if (!isInPreviewMode && !hasExistingStyles && !isCharacterOrWordAnimation) {
      [charStyleId, wordStyleId, customAnimationId].forEach(styleId => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      });
    }

    const styles = processSingleAnimation(
      activeAnimation,
      elementData,
      pageId,
      zoom,
      activeAnimation.relativeTime,
      isPaused,
      currentPlayingTime
    );

    return styles;
  }

  // Handle single animation (legacy format)
  // When paused, treat it like playing so we can apply animation styles
  if (!(isPlaying || isPaused || elementData.cssAnimation.isPreview)) {
    // Even when not playing, check if element should be hidden due to animate-in or start stage
    if (
      elementData.cssAnimation.transition === "in" ||
      elementData.cssAnimation.stage === ANIMATION_STAGES.START
    ) {
      // Element has an animate-in animation or start stage - should be hidden initially
      return {
        opacity: 0,
        visibility: "hidden"
      };
    }

    return null;
  }

  // For single animations, check if it's an animate-in or start stage that hasn't started yet
  if (
    (isPlaying || isPaused) &&
    (elementData.cssAnimation.transition === "in" ||
      elementData.cssAnimation.stage === ANIMATION_STAGES.START)
  ) {
    const animationStartTime = elementData.cssAnimation.delay || 0;

    if (currentPlayingTime < animationStartTime) {
      // Element has an animate-in animation or start stage that hasn't started yet - should be hidden
      return {
        opacity: 0,
        visibility: "hidden"
      };
    }
  }

  // Only clean up existing animation styles if we're not currently in preview mode
  // This prevents animation flickering during mouse movements while preview is active
  const animationId = `text-anim-${elementData.uniqueId}`;
  const charStyleId = `char-slide-animation-${animationId}`;
  const wordStyleId = `word-animation-${animationId}`;
  const customAnimName = `custom${elementData.cssAnimation.name}-${elementData.cssAnimation.direction}-${elementData.cssAnimation.transition}-${elementData.uniqueId}`;
  const customAnimationId = `custom-animation-${customAnimName}`;

  // Check if we're in preview mode and styles already exist
  const isInPreviewMode = elementData.cssAnimation.isPreview;
  const hasExistingStyles =
    document.getElementById(charStyleId) ||
    document.getElementById(wordStyleId) ||
    document.getElementById(customAnimationId);

  // Only clean up if not in preview mode OR if no existing styles are found
  if (!isInPreviewMode || !hasExistingStyles) {
    [charStyleId, wordStyleId, customAnimationId].forEach(styleId => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    });
  }

  return processSingleAnimation(
    elementData.cssAnimation,
    elementData,
    pageId,
    zoom,
    0,
    isPaused,
    currentPlayingTime
  );
};

/**
 * Combines element style with animation styles if present
 * @param {Object} elementStyle - Base element styles
 * @param {Object} elementData - Element data containing animation config
 * @param {boolean} isPlaying - Whether the animation is playing
 * @param {number} pageId - Page ID
 * @param {number} zoom - Zoom level
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @param {boolean} isPaused - Whether timeline is paused
 * @returns {Object} Combined style object
 */
export const getCombinedAnimatedStyles = (
  elementStyle,
  elementData,
  isPlaying,
  pageId,
  zoom,
  currentPlayingTime = 0,
  isPaused = false
) => {
  const animationStyles = getAnimationStyles(
    elementData,
    isPlaying,
    pageId,
    zoom,
    currentPlayingTime,
    isPaused
  );

  if (!animationStyles) {
    return elementStyle;
  }

  return {
    ...elementStyle,
    ...animationStyles
  };
};

export const shouldAnimateElement = (
  elementData,
  isPlaying,
  isPaused = false
) => {
  if (!elementData.cssAnimation) {
    return false;
  }

  // Handle multiple animations (array format)
  if (Array.isArray(elementData.cssAnimation)) {
    return Boolean(
      isPlaying ||
        isPaused ||
        elementData.cssAnimation.some(anim => anim.isPreview)
    );
  }

  // Handle single animation (legacy format)
  return Boolean(isPlaying || isPaused || elementData.cssAnimation?.isPreview);
};

/**
 * Calculates the total duration of an animation for all elements
 * @param {Array|Object} elements - Single element or array of elements being animated
 * @returns {number} Total animation duration in milliseconds
 */
export const calculateTotalAnimationDuration = elements => {
  // Ensure elements is an array
  const elementArray = Array.isArray(elements) ? elements : [elements];

  let maxDuration = 0;

  elementArray.forEach(element => {
    let elementDuration = 0;

    // Handle new cssAnimation array structure (multiple animations)
    if (element?.cssAnimation && Array.isArray(element.cssAnimation)) {
      const animations = element.cssAnimation;

      if (animations.length === 0) {
        elementDuration = 0;
      } else if (animations.length === 1) {
        // Single animation - calculate its duration
        const animation = animations[0];
        elementDuration = calculateSingleAnimationDuration(animation, element);
      } else {
        // Multiple animations - calculate from first start to last end based on stage
        // Find first and last animations by stage priority (start -> middle -> end)
        const stageOrder = [
          ANIMATION_STAGES.START,
          ANIMATION_STAGES.MIDDLE,
          ANIMATION_STAGES.END
        ];

        // Find the earliest stage animation (first to play)
        let firstAnimation = null;
        for (const stage of stageOrder) {
          firstAnimation = animations.find(anim => anim.stage === stage);
          if (firstAnimation) break;
        }

        // Find the latest stage animation (last to play)
        let lastAnimation = null;
        for (let i = stageOrder.length - 1; i >= 0; i--) {
          const stage = stageOrder[i];
          lastAnimation = animations.find(anim => anim.stage === stage);
          if (lastAnimation) break;
        }

        // Fallback to index-based sorting if stage-based approach fails
        if (!firstAnimation || !lastAnimation) {
          firstAnimation = animations[0];
          lastAnimation = animations[animations.length - 1];
        }

        const firstStartTime = firstAnimation.delay || 0;
        const lastStartTime = lastAnimation.delay || 0;
        const lastAnimationDuration = calculateSingleAnimationDuration(
          lastAnimation,
          element
        );

        // Total duration = (last animation start time + last animation duration) - first animation start time
        elementDuration =
          lastStartTime + lastAnimationDuration - firstStartTime;
      }
    }
    // Handle legacy cssAnimation object structure (single animation)
    else if (
      element?.cssAnimation &&
      typeof element.cssAnimation === "object"
    ) {
      elementDuration = calculateSingleAnimationDuration(
        element.cssAnimation,
        element
      );
    }
    // No animation
    else {
      elementDuration = 0;
    }

    maxDuration = Math.max(maxDuration, elementDuration);
  });

  return maxDuration;
};

/**
 * Calculates the duration of a single animation
 * @param {Object} animation - Single animation object
 * @param {Object} element - Element data for textbox duration calculations
 * @returns {number} Animation duration in milliseconds
 */
export const calculateSingleAnimationDuration = (animation, element) => {
  let animationDuration = ANIMATION_DURATION; // Base duration (1000ms)

  // For textbox elements, calculate additional duration based on text type
  // Note: element.type might be missing in some contexts, so also check if it's a text animation
  if (
    (element?.type === "textbox" ||
      (animation?.textType &&
        (animation.textType === "letter" || animation.textType === "word"))) &&
    animation?.textType
  ) {
    const textType = animation.textType;

    // If textType is "textbox", just use base duration
    if (textType === "textbox") {
      animationDuration = ANIMATION_DURATION;
    } else if (textType === "letter" || textType === "word") {
      // Calculate character count for letter/word animations
      const displayValue = element.displayValue || "";
      const charCount = displayValue.replace(/<[^>]*>/g, "").length; // Strip HTML tags but keep spaces for counting

      if (charCount > 0) {
        // Calculate when the last character/word finishes animating
        // Last character/word delay + base animation duration = total duration
        const lastCharDelay =
          textType === "word"
            ? (Math.ceil(charCount / 5) - 1) * CHARACTER_DELAY // Approximate words by dividing chars by 5, subtract 1 because first word has 0 delay
            : (charCount - 1) * CHARACTER_DELAY; // Letter animations, subtract 1 because first letter has 0 delay

        animationDuration = lastCharDelay + ANIMATION_DURATION;
      }
    }
  }

  return animationDuration;
};
